// Shared shapes for the Findings page: severity/lifecycle vocab, MITRE names,
// and parsing for the free-text columns (notes, assigned_to) on findings.

export const SEVS = ['critical', 'high', 'medium', 'low', 'info'];
export const SEV_LABEL = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info' };
const SEV_TOKEN = { critical: 'crit', high: 'high', medium: 'med', low: 'low' };
const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

export const sevKey = (s) => (SEV_RANK[s] == null ? 'info' : s);
export const sevRank = (s) => SEV_RANK[sevKey(s)];
export const sevFg = (s) => (SEV_TOKEN[s] ? `var(--sev-${SEV_TOKEN[s]}-fg)` : 'transparent');
export const sevBg = (s) => (SEV_TOKEN[s] ? `var(--sev-${SEV_TOKEN[s]}-bg)` : 'var(--surf-raised)');
// Info has no colour of its own, so its dot is an outline.
export const sevDotBorder = (s) => (SEV_TOKEN[s] ? 'transparent' : 'var(--ink-faint)');

export const STAGES = ['new', 'triaged', 'investigating', 'contained', 'resolved'];
export const STAGE_LABEL = { new: 'New', triaged: 'Triaged', investigating: 'Investigating', contained: 'Contained', resolved: 'Resolved' };
export const OPEN_STAGES = STAGES.filter(s => s !== 'resolved');
export const NEXT = {
  new: ['triaged', 'Mark triaged'],
  triaged: ['investigating', 'Start investigating'],
  investigating: ['contained', 'Mark contained'],
  contained: ['resolved', 'Resolve'],
  resolved: ['new', 'Reopen'],
};
// The schema has no investigating_at, so that stage never shows a time.
export const STAGE_TIME = { new: 'detected_at', triaged: 'triaged_at', contained: 'contained_at', resolved: 'resolved_at' };

export const MITRE = {
  T1003: 'OS Credential Dumping', 'T1003.001': 'LSASS Memory', T1008: 'Fallback Channels', T1020: 'Automated Exfiltration',
  T1021: 'Remote Services', T1027: 'Obfuscated Files or Information', T1041: 'Exfiltration Over C2 Channel',
  T1046: 'Network Service Discovery', T1053: 'Scheduled Task/Job', T1055: 'Process Injection',
  T1059: 'Command and Scripting Interpreter', 'T1059.001': 'PowerShell', T1071: 'Application Layer Protocol',
  'T1071.001': 'Web Protocols', 'T1071.002': 'File Transfer Protocols', T1074: 'Data Staged', T1078: 'Valid Accounts',
  T1090: 'Proxy', T1098: 'Account Manipulation', T1102: 'Web Service', T1105: 'Ingress Tool Transfer', T1106: 'Native API',
  T1110: 'Brute Force', T1119: 'Automated Collection', T1133: 'External Remote Services', T1136: 'Create Account',
  T1189: 'Drive-by Compromise', T1190: 'Exploit Public-Facing Application', T1197: 'BITS Jobs', T1199: 'Trusted Relationship',
  T1203: 'Exploitation for Client Execution', T1204: 'User Execution', 'T1204.002': 'Malicious File',
  T1485: 'Data Destruction', T1486: 'Data Encrypted for Impact', T1490: 'Inhibit System Recovery',
  T1505: 'Server Software Component', T1530: 'Data from Cloud Storage', T1543: 'Create or Modify System Process',
  'T1543.002': 'Systemd Service', 'T1543.003': 'Windows Service', T1546: 'Event Triggered Execution',
  T1550: 'Use Alternate Authentication Material', T1555: 'Credentials from Password Stores', T1559: 'Inter-Process Communication',
  T1560: 'Archive Collected Data', T1566: 'Phishing', 'T1566.002': 'Spearphishing Link', T1567: 'Exfiltration Over Web Service',
  T1570: 'Lateral Tool Transfer', T1571: 'Non-Standard Port', T1572: 'Protocol Tunneling', T1573: 'Encrypted Channel',
  T1583: 'Acquire Infrastructure', T1584: 'Compromise Infrastructure', T1586: 'Compromise Accounts',
  T1587: 'Develop Capabilities', T1588: 'Obtain Capabilities', T1590: 'Gather Victim Network Information',
  T1592: 'Gather Victim Host Information', T1595: 'Active Scanning', T1597: 'Search Closed Sources',
  T1598: 'Phishing for Information', T1608: 'Stage Capabilities',
};
export const mitreUrl = (id) => `https://attack.mitre.org/techniques/${id.replace('.', '/')}/`;

// Some rows hold the literal string "null" from an earlier JSON.stringify.
export const clean = (v) => (v == null || v === 'null' || v === '' ? null : v);

// Notes are stored as a JSON array of { who, email, at, text }. Older rows
// hold plain text, which shows as one unattributed note.
export const parseNotes = (raw) => {
  const v = clean(raw);
  if (!v) return [];
  if (Array.isArray(v)) return v;
  try {
    const parsed = JSON.parse(v);
    if (Array.isArray(parsed)) return parsed.filter(n => n && n.text);
  } catch { /* plain text */ }
  return [{ who: null, at: null, text: String(v) }];
};

export const evidenceRows = (ev) => {
  if (!ev || typeof ev !== 'object') return [];
  return Object.entries(ev)
    .filter(([k, v]) => k !== 'rule_id' && k !== 'false_positive_hints' && v != null && v !== '')
    .map(([k, v]) => [
      k.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()),
      typeof v === 'object' ? JSON.stringify(v) : String(v),
    ]);
};
